import { useEffect, useState } from "react";
import { ScreenLoading, Warn } from "../components";
import { api, type AdminUser } from "../lib/api";

/**
 * User management.
 *
 * Behind the user.manage capability, which admin holds and the other three roles
 * do not -- and admin is deliberately not a superset, so an IT admin creating
 * accounts cannot also record an approval. That separation is the reason this
 * page can be this simple: it has no opinion about permissions, because
 * permissions are a per-role fact and the roles are fixed.
 *
 * No self-service password reset anywhere, because there is no mail server. A
 * forgotten password is an admin action, deliberately.
 */

const ROLES: [string, string, string][] = [
  ["surveyor", "Surveyor", "Menggambar area, mencatat persetujuan dan keterangan di lapangan."],
  ["supervisor", "Supervisor", "Semua surveyor, ditambah mengubah batas area dan persetujuan internal."],
  ["management", "Manajemen", "Hanya membaca. Tidak dapat membuka berkas bukti."],
  ["admin", "Admin", "Mengelola user. Tidak dapat mencatat persetujuan."],
];

export function Users({ say }: { say: (tone: "red" | "green" | "blue", text: string) => void }) {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState<string[]>(["surveyor"]);

  async function load() {
    try {
      setUsers(await api.users());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal memuat user.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function toggle(r: string) {
    setRoles((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  }

  async function create() {
    setBusy(true);
    setError(null);
    try {
      await api.createUser({ display_name: name, email, password, roles });
      say("green", `Akun ${email} dibuat.`);
      setName("");
      setEmail("");
      setPassword("");
      setRoles(["surveyor"]);
      setCreating(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal membuat akun.");
    } finally {
      setBusy(false);
    }
  }

  async function setActive(u: AdminUser, active: boolean) {
    try {
      await api.updateUser(u.id, { active });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal mengubah status akun.");
    }
  }

  async function resetPassword(u: AdminUser) {
    const pwd = window.prompt(`Kata sandi baru untuk ${u.display_name} (minimal 8 karakter):`);
    if (!pwd) return;
    try {
      await api.updateUser(u.id, { password: pwd });
      say("green", `Kata sandi ${u.display_name} diperbarui.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Gagal memperbarui kata sandi.");
    }
  }

  return (
    <div className="page">
      <div className="rowflex mb16">
        <div>
          <h1 className="page-title" style={{ marginBottom: 2 }}>
            User
          </h1>
          <p className="page-sub" style={{ margin: 0 }}>
            Peran adalah kemampuan, bukan jenjang. Admin tidak otomatis bisa mencatat persetujuan.
          </p>
        </div>
        <div className="spacer" />
        <button className="btn btn-primary" onClick={() => setCreating((c) => !c)}>
          {creating ? "Tutup" : "+ Akun Baru"}
        </button>
      </div>

      {error && (
        <div className="mb16">
          <Warn tone="red">{error}</Warn>
        </div>
      )}

      {creating && (
        <div className="panel">
          <h3>Akun baru</h3>
          <div className="stack">
            <div className="grid2">
              <div className="field">
                <label htmlFor="u-name">Nama</label>
                <input id="u-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="u-email">Email</label>
                <input
                  id="u-email"
                  className="input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="field">
              <label htmlFor="u-pw">Kata sandi (minimal 8 karakter)</label>
              <input
                id="u-pw"
                className="input"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span className="small mute">
                Beri tahu user secara langsung. Tidak ada pemulihan kata sandi mandiri karena tidak ada
                server email.
              </span>
            </div>
            <div>
              <div className="small mute mb8">Peran</div>
              <div className="stack" style={{ gap: 6 }}>
                {ROLES.map(([key, label, desc]) => (
                  <label className="rowflex small" key={key} style={{ alignItems: "flex-start" }}>
                    <input
                      type="checkbox"
                      checked={roles.includes(key)}
                      onChange={() => toggle(key)}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <strong>{label}</strong> — <span className="mute">{desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <button
                className="btn btn-primary"
                disabled={busy || roles.length === 0}
                onClick={create}
              >
                {busy ? "Menyimpan…" : "Buat Akun"}
              </button>
            </div>
          </div>
        </div>
      )}

      {users === null ? (
        <ScreenLoading label="Memuat user…" />
      ) : (
        // Both presentations render; the breakpoint picks. A five-column
        // table cannot fit a phone (sideways scroll buries the actions),
        // and cards waste a desktop. The account count is small, so the
        // duplicated DOM costs nothing.
        <>
        <div className="panel only-desktop">
          <table>
            <thead>
              <tr>
                <th>Nama</th>
                <th>Email</th>
                <th>Peran</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.display_name}
                    {u.email.startsWith("demo+") && (
                      <span className="chip chip-pending" style={{ marginLeft: 6 }}>
                        demo
                      </span>
                    )}
                  </td>
                  <td className="small mute">{u.email}</td>
                  <td>
                    <div className="rowflex wrap">
                      {u.roles.map((r) => (
                        <span className="chip" key={r}>
                          {r}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <span className={`chip ${u.active ? "chip-granted" : "chip-refused"}`}>
                      {u.active ? "aktif" : "nonaktif"}
                    </span>
                  </td>
                  <td>
                    <div className="rowflex" style={{ justifyContent: "flex-end" }}>
                      <button className="btn btn-sm" onClick={() => void resetPassword(u)}>
                        Atur sandi
                      </button>
                      <button className="btn btn-sm" onClick={() => void setActive(u, !u.active)}>
                        {u.active ? "Nonaktifkan" : "Aktifkan"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="stack only-phone">
          {users.map((u) => (
            <div className="panel" key={u.id}>
              <div className="rowflex wrap">
                <strong>{u.display_name}</strong>
                {u.email.startsWith("demo+") && <span className="chip chip-pending">demo</span>}
                <span className="spacer" />
                <span className={`chip ${u.active ? "chip-granted" : "chip-refused"}`}>
                  {u.active ? "aktif" : "nonaktif"}
                </span>
              </div>
              <div className="small mute" style={{ margin: "4px 0 8px" }}>
                {u.email}
              </div>
              <div className="rowflex wrap" style={{ marginBottom: 10 }}>
                {u.roles.map((r) => (
                  <span className="chip" key={r}>
                    {r}
                  </span>
                ))}
              </div>
              <div className="rowflex wrap">
                <button className="btn btn-sm" onClick={() => void resetPassword(u)}>
                  Atur sandi
                </button>
                <button className="btn btn-sm" onClick={() => void setActive(u, !u.active)}>
                  {u.active ? "Nonaktifkan" : "Aktifkan"}
                </button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}

      <div className="panel">
        <h3>Arti setiap peran</h3>
        <div className="stack">
          {ROLES.map(([key, label, desc]) => (
            <div key={key}>
              <strong>{label}</strong> <span className="mono mute">({key})</span>
              <div className="small dim">{desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
